<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
            <div class="md:flex md:items-center md:justify-between">
                <div class="flex-1 min-w-0">
                    <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Logs') }}</h1>
                </div>
                <div class="mt-4 flex md:mt-0 md:ml-4">
                    <router-link class="btn btn-gray shadow-sm rounded" to="/workspace/admin/settings">
                        {{ $t('Back') }}
                    </router-link>
                </div>
            </div>
        </div>

        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="my-6">
                <loading :status="loading"/>

                <div class="shadow sm:rounded sm:overflow-hidden">
                    <div class="px-4 py-5 bg-white sm:p-6 space-y-6">
                        <div class="inline-flex rounded border border-gray-200 overflow-hidden">
                            <button
                                type="button"
                                class="px-4 py-2 text-sm font-medium transition"
                                :class="activeTab === 'files' ? 'bg-gray-700 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                                @click="activeTab = 'files'"
                            >
                                {{ $t('Log files') }}
                            </button>
                            <button
                                type="button"
                                class="px-4 py-2 text-sm font-medium transition border-l border-gray-200"
                                :class="activeTab === 'failedJobs' ? 'bg-gray-700 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                                @click="openFailedJobs"
                            >
                                {{ $t('Failed jobs') }} ({{ failedJobsCount }})
                            </button>
                        </div>

                        <div v-if="activeTab === 'files'" class="grid grid-cols-1 lg:grid-cols-12 gap-6">
                            <div class="lg:col-span-4">
                                <div class="border border-gray-200 rounded">
                                    <div class="px-4 py-3 border-b border-gray-200">
                                        <h2 class="text-sm font-semibold text-gray-900">{{ $t('Log files') }}</h2>
                                    </div>
                                    <div class="p-2 space-y-1 max-h-[34rem] overflow-y-auto">
                                        <button
                                            v-for="file in files"
                                            :key="file.name"
                                            class="w-full text-left px-3 py-2 rounded border transition"
                                            :class="selectedFile === file.name ? 'border-gray-400 bg-gray-100 text-gray-900' : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'"
                                            @click="selectFile(file.name)"
                                        >
                                            <div class="text-sm font-medium truncate">{{ file.name }}</div>
                                            <div class="text-xs text-gray-500">{{ formatBytes(file.size) }}</div>
                                        </button>
                                        <div v-if="!files.length" class="text-sm text-gray-500 p-2">
                                            {{ $t('No log files found') }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="lg:col-span-8 space-y-3">
                                <div class="border border-gray-200 rounded p-3 sm:p-4">
                                    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <div class="text-sm text-gray-700">
                                            <span class="font-semibold">{{ $t('Selected file') }}:</span>
                                            <span>{{ selectedFile || '-' }}</span>
                                        </div>
                                        <div class="flex flex-wrap gap-2">
                                            <button class="btn btn-gray rounded shadow-sm" type="button" :disabled="!selectedFile || loadingFile" @click="refreshFile">
                                                {{ $t('Refresh') }}
                                            </button>
                                            <button class="btn btn-gray rounded shadow-sm" type="button" :disabled="!selectedFile || loadingFile" @click="downloadFile">
                                                {{ $t('Download') }}
                                            </button>
                                            <button class="btn btn-red rounded shadow-sm" type="button" :disabled="!selectedFile || loadingFile" @click="clearFile">
                                                {{ $t('Clear log') }}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-gray-500">
                                    <div>{{ $t('Showing the last 500 lines') }}</div>
                                    <div class="flex items-center gap-2">
                                        <label class="text-xs text-gray-600">{{ $t('Level') }}</label>
                                        <select v-model="levelFilter" class="form-input py-1 h-8 text-xs">
                                            <option value="all">{{ $t('All') }}</option>
                                            <option value="EMERGENCY">EMERGENCY</option>
                                            <option value="ALERT">ALERT</option>
                                            <option value="CRITICAL">CRITICAL</option>
                                            <option value="ERROR">ERROR</option>
                                            <option value="WARNING">WARNING</option>
                                            <option value="NOTICE">NOTICE</option>
                                            <option value="INFO">INFO</option>
                                            <option value="DEBUG">DEBUG</option>
                                        </select>
                                    </div>
                                </div>

                                <div v-if="parsedEntries.length" class="border border-gray-200 rounded bg-gray-50 max-h-screen overflow-y-auto">
                                    <div
                                        v-for="(entry, idx) in filteredEntries"
                                        :key="`${idx}-${entry.timestamp}-${entry.level}`"
                                        class="p-4 border-b border-gray-200 last:border-b-0 bg-white"
                                    >
                                        <div class="flex items-start justify-between gap-3">
                                            <div class="text-xs text-gray-500">{{ entry.timestamp }}</div>
                                            <span class="px-2 py-0.5 rounded text-xs font-semibold" :class="levelClass(entry.level)">{{ entry.level }}</span>
                                        </div>
                                        <div class="mt-2 text-sm text-gray-900 break-words leading-6">{{ entry.message }}</div>
                                        <button
                                            v-if="entry.details"
                                            type="button"
                                            class="mt-2 text-xs text-blue-700 hover:underline"
                                            @click="toggleDetails(idx)"
                                        >
                                            {{ expandedEntries[idx] ? $t('Hide details') : $t('Show details') }}
                                        </button>
                                        <pre v-if="entry.details && expandedEntries[idx]" class="mt-2 bg-gray-50 border border-gray-200 rounded p-2 text-xs leading-5 overflow-x-auto">{{ entry.details }}</pre>
                                    </div>
                                </div>

                                <textarea
                                    v-else
                                    class="form-input block w-full font-mono text-xs leading-5 border-gray-200"
                                    rows="26"
                                    :value="content"
                                    readonly
                                ></textarea>
                            </div>
                        </div>

                        <div v-if="activeTab === 'failedJobs'" class="space-y-4">
                            <div class="border border-gray-200 rounded p-3 sm:p-4 flex items-center justify-between">
                                <div class="text-sm text-gray-700">
                                    <span class="font-semibold">{{ $t('Failed jobs') }}:</span>
                                    <span>{{ failedJobsCount }}</span>
                                </div>
                                <button class="btn btn-gray rounded shadow-sm" type="button" :disabled="loadingFailedJobs" @click="fetchFailedJobs">
                                    {{ $t('Refresh') }}
                                </button>
                            </div>

                            <div v-if="!failedJobs.length" class="text-sm text-gray-500 border border-gray-200 rounded p-4">
                                {{ $t('No failed jobs found') }}
                            </div>

                            <div v-for="(job, idx) in failedJobs" :key="job.id" class="border border-gray-200 rounded p-4 bg-white">
                                <div class="text-sm font-semibold text-gray-900">#{{ job.id }} - {{ job.job || 'Unknown Job' }}</div>
                                <div class="text-xs text-gray-500 mt-1">{{ job.failed_at }} | {{ job.connection }} / {{ job.queue }}</div>
                                <div class="mt-2 text-sm text-red-700 break-words">{{ job.exception_preview }}</div>
                                <button type="button" class="mt-2 text-xs text-blue-700 hover:underline" @click="toggleFailedJob(idx)">
                                    {{ expandedFailedJobs[idx] ? $t('Hide details') : $t('Show details') }}
                                </button>
                                <pre v-if="expandedFailedJobs[idx]" class="mt-2 bg-gray-50 border border-gray-200 rounded p-2 text-xs leading-5 overflow-x-auto">{{ job.exception }}</pre>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
export default {
    name: "logs",
    data() {
        return {
            loading: true,
            loadingFile: false,
            loadingFailedJobs: false,
            activeTab: 'files',
            files: [],
            selectedFile: null,
            content: '',
            levelFilter: 'all',
            expandedEntries: {},
            failedJobsCount: 0,
            failedJobs: [],
            expandedFailedJobs: {},
        }
    },
    computed: {
        parsedEntries() {
            const lines = (this.content || '').split(/\r\n|\n|\r/);
            const headerRegex = /^\[(.+?)\]\s+([^.\s]+)\.([A-Z]+):\s?(.*)$/;
            const entries = [];
            let current = null;

            for (const line of lines) {
                const match = line.match(headerRegex);
                if (match) {
                    if (current) {
                        entries.push(current);
                    }
                    current = {
                        timestamp: match[1],
                        level: match[3],
                        message: match[4],
                        details: ''
                    };
                } else if (current) {
                    current.details = current.details ? `${current.details}\n${line}` : line;
                }
            }

            if (current) {
                entries.push(current);
            }

            return entries;
        },
        filteredEntries() {
            if (this.levelFilter === 'all') {
                return this.parsedEntries;
            }
            return this.parsedEntries.filter(entry => entry.level === this.levelFilter);
        }
    },
    mounted() {
        this.get();
    },
    methods: {
        get() {
            const self = this;
            self.loading = true;
            axios.get('api/workspace/admin/settings/logs').then(function (response) {
                self.files = response.data.files || [];
                self.selectedFile = response.data.selected_file;
                self.content = response.data.content || '';
                self.failedJobsCount = response.data.failed_jobs_count || 0;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
                self.$router.push('/workspace/admin/settings');
            });
        },
        selectFile(file) {
            if (!file) {
                return;
            }

            const self = this;
            self.loadingFile = true;
            axios.get(`api/workspace/admin/settings/logs/${encodeURIComponent(file)}`).then(function (response) {
                self.selectedFile = response.data.file;
                self.content = response.data.content || '';
                self.expandedEntries = {};
                self.loadingFile = false;
            }).catch(function () {
                self.loadingFile = false;
            });
        },
        refreshFile() {
            if (!this.selectedFile) {
                return;
            }
            this.selectFile(this.selectedFile);
        },
        downloadFile() {
            if (!this.selectedFile) {
                return;
            }

            const self = this;
            self.loadingFile = true;
            axios.get(`api/workspace/admin/settings/logs/${encodeURIComponent(self.selectedFile)}/download`, {
                responseType: 'blob'
            }).then(function (response) {
                const blob = new Blob([response.data], {type: 'text/plain;charset=utf-8'});
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', self.selectedFile);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                self.loadingFile = false;
            }).catch(function () {
                self.loadingFile = false;
            });
        },
        clearFile() {
            if (!this.selectedFile) {
                return;
            }
            if (!window.confirm(this.$t('Are you sure you want to clear this log file?'))) {
                return;
            }

            const self = this;
            self.loadingFile = true;
            axios.delete(`api/workspace/admin/settings/logs/${encodeURIComponent(self.selectedFile)}`).then(function () {
                self.content = '';
                self.loadingFile = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Logs cleared successfully').toString(),
                    type: 'success'
                });
                self.get();
            }).catch(function () {
                self.loadingFile = false;
            });
        },
        openFailedJobs() {
            this.activeTab = 'failedJobs';
            this.fetchFailedJobs();
        },
        fetchFailedJobs() {
            const self = this;
            self.loadingFailedJobs = true;
            axios.get('api/workspace/admin/settings/failed-jobs').then(function (response) {
                self.failedJobsCount = response.data.count || 0;
                self.failedJobs = response.data.items || [];
                self.loadingFailedJobs = false;
            }).catch(function () {
                self.loadingFailedJobs = false;
            });
        },
        toggleDetails(idx) {
            this.expandedEntries[idx] = !this.expandedEntries[idx];
        },
        toggleFailedJob(idx) {
            this.expandedFailedJobs[idx] = !this.expandedFailedJobs[idx];
        },
        levelClass(level) {
            const map = {
                EMERGENCY: 'bg-red-100 text-red-700',
                ALERT: 'bg-red-100 text-red-700',
                CRITICAL: 'bg-red-100 text-red-700',
                ERROR: 'bg-red-100 text-red-700',
                WARNING: 'bg-yellow-100 text-yellow-700',
                NOTICE: 'bg-blue-100 text-blue-700',
                INFO: 'bg-blue-100 text-blue-700',
                DEBUG: 'bg-gray-100 text-gray-700',
            };
            return map[level] || 'bg-gray-100 text-gray-700';
        },
        formatBytes(value) {
            if (!value) {
                return '0 B';
            }

            const units = ['B', 'KB', 'MB', 'GB', 'TB'];
            const i = Math.floor(Math.log(value) / Math.log(1024));
            return `${(value / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
        }
    }
}
</script>
